import type { ConsumoTotem, PayloadNovoPedido } from '../api';
import type { ItemCarrinho } from '../store/carrinho-slice';

/**
 * Montagem do pedido que vai ao backend, pura e fora da tela.
 *
 * O `checkout-final` monta o pedido de QUATRO canais (delivery, mesa/comanda,
 * tablet e totem) dentro de um componente de ~2 mil linhas. Com o totem saindo
 * para o app próprio, ou a montagem virava função única ou passariam a existir
 * duas cópias — e campo novo entraria só numa delas. Aqui a regra é uma só; cada
 * tela resolve os SEUS valores e chama.
 */

/** Itens do carrinho no formato do pedido. Passo sem item escolhido não vai. */
export function itensDoPayload(itens: ItemCarrinho[]): PayloadNovoPedido['itens'] {
  return itens.map((i) => ({
    idProduto: i.idProduto,
    nome: i.nome,
    quantidade: i.quantidade,
    valorUnitario: i.precoUnitario,
    valorTotal: i.precoUnitario * i.quantidade,
    observacao: i.observacao?.trim() || undefined,
    passos:
      (i.passos?.filter((p) => p.itens.length > 0) as PayloadNovoPedido['itens'][number]['passos'] | undefined) ??
      undefined,
  }));
}

/**
 * Impressão do totem: o aparelho imprime sozinho (Android) OU manda o local
 * configurado nele. Sem nenhum dos dois o backend cai na configuração da loja,
 * que é o comportamento de quem ainda não configurou o totem.
 */
export function camposImpressaoTotem(config: {
  imprimeNoAparelho: boolean;
  localImpressao: string;
}): Pick<PayloadNovoPedido, 'imprimeNoAparelho' | 'localImpressao'> {
  if (config.imprimeNoAparelho) return { imprimeNoAparelho: true };
  const local = config.localImpressao.trim();
  return local ? { localImpressao: local } : {};
}

export interface EntradaPayloadPedido {
  cliente: PayloadNovoPedido['cliente'];
  itens: ItemCarrinho[];
  identificadorPrincipalNumero?: string;
  identificadorSecundarioNumero?: string;
  observacaoGeral?: string;
  horarioAgendamento?: string;
  metodoEntrega?: PayloadNovoPedido['metodoEntrega'];
  consumoTotem?: ConsumoTotem;
  /** Só no totem; ausente nos demais canais. */
  impressaoTotem?: { imprimeNoAparelho: boolean; localImpressao: string };
  /** Como o cliente digitou; vazio ou não numérico não vai. */
  codigoPager?: string;
  enderecoEntrega?: PayloadNovoPedido['enderecoEntrega'];
  /** `null` = canal sem pagamento no cardápio (mesa/comanda e tablet). */
  formaPagamento: { id: number; codigo: string } | null;
  /** Só vai quando é dinheiro e o cliente informou. */
  troco?: number;
  cupom?: string;
  subtotal: number;
  /** Já com a taxa de conveniência embutida, para o total bater no Sischef. */
  taxaEntrega: number;
  freteComDesconto?: boolean
  desconto: number;
  total: number;
  /** Cashback usado como desconto (só delivery). */
  valorCashback?: number;
}

export function montarPayloadPedido(entrada: EntradaPayloadPedido): PayloadNovoPedido {
  const pager = entrada.codigoPager?.trim();

  return {
    cliente: entrada.cliente,
    identificadorSecundarioNumero: entrada.identificadorSecundarioNumero,
    identificadorPrincipalNumero: entrada.identificadorPrincipalNumero,
    observacaoGeral: entrada.observacaoGeral,
    horarioAgendamento: entrada.horarioAgendamento,
    metodoEntrega: entrada.metodoEntrega,
    consumoTotem: entrada.consumoTotem,
    ...(entrada.impressaoTotem ? camposImpressaoTotem(entrada.impressaoTotem) : {}),
    ...(pager ? { codigoPager: Number(pager) } : {}),
    itens: itensDoPayload(entrada.itens),
    enderecoEntrega: entrada.enderecoEntrega,
    formaPagamento: entrada.formaPagamento?.codigo,
    idFormaPagamento: entrada.formaPagamento?.id,
    troco: entrada.troco,
    cupom: entrada.cupom,
    subtotal: entrada.subtotal,
    taxaEntrega: entrada.taxaEntrega,
    freteComDesconto: entrada.freteComDesconto,
    desconto: entrada.desconto,
    total: entrada.total,
    // O backend recusa `valorCashback` 0 ou null: sem cashback o campo não vai.
    ...(entrada.valorCashback && entrada.valorCashback > 0 ? { valorCashback: entrada.valorCashback } : {}),
  };
}
